import { SearchInput } from '@d-edge/overnight-hotelier-react'
import { useState } from 'react'
import { useBookingEngineDecision, useBrokenBookingEngines } from '@/api/queries'
import type { BookingEngineDecision, BrokenBookingEngine } from '@/api/types'
import { DataTable, type Column } from '@/components/DataTable/DataTable'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { KeyValueList } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/Toast'
import { ago } from '@/lib/format'
import { useDebouncedValue } from '@/lib/hooks'

/** Hotels whose detected booking engine differs from the configured one. */
export function BrokenBookingEnginesDialog({ onClose }: { onClose: () => void }) {
  const [filter, setFilter] = useState('')
  const debounced = useDebouncedValue(filter, 300)
  const { data, isLoading, error } = useBrokenBookingEngines(debounced, true)
  const decide = useBookingEngineDecision()
  const toast = useToast()
  const [pending, setPending] = useState<{ entry: BrokenBookingEngine; decision: BookingEngineDecision } | null>(null)

  const apply = async (entry: BrokenBookingEngine, decision: BookingEngineDecision) => {
    setPending(null)
    try {
      await decide.mutateAsync({ entry, decision })
      toast.notify(`${entry.hotelName ?? entry.competitorId}: ${decision.toLowerCase()}`, 'success')
    } catch (err) {
      toast.error(err, 'Could not record the decision')
    }
  }

  const columns: Column<BrokenBookingEngine>[] = [
    {
      key: 'hotelName',
      header: 'Hotel',
      render: (b) => (b.hotelWebsite ? <a href={b.hotelWebsite} target="_blank" rel="noreferrer">{b.hotelName ?? b.hotelWebsite}</a> : b.hotelName),
    },
    { key: 'competitorId', header: 'Competitor', align: 'right' },
    { key: 'currentBookingEngineName', header: 'Configured' },
    { key: 'newBookingEngineName', header: 'Detected' },
    { key: 'detectedAt', header: 'Detected', render: (b) => ago(b.detectedAt) },
    {
      key: 'actions',
      header: '',
      sortable: false,
      width: '230px',
      render: (b) => (
        <span className="u-row">
          <Button size="sm" variant="primary" disabled={!b.newBookingEngineParams || b.newBookingEngineName === 'Unknown'} onClick={() => setPending({ entry: b, decision: 'Accepted' })} title="Apply the detected booking engine">
            Apply
          </Button>
          <Button size="sm" onClick={() => setPending({ entry: b, decision: 'Rejected' })} title="Keep the current configuration">
            Reject
          </Button>
          <Button size="sm" variant="ghost" onClick={() => void apply(b, 'Ignored')} title="Hide for 30 days">
            Ignore
          </Button>
        </span>
      ),
    },
  ]

  return (
    <Modal
      title="Invalid booking engines"
      onClose={onClose}
      size="xl"
      headerExtra={<SearchInput icon placeholder="Filter by configured booking engine" aria-label="Filter by configured booking engine" value={filter} onChange={setFilter} clearButtonLabel="Clear filter" style={{ minWidth: 320 }} />}
    >
      <DataTable
        columns={columns}
        rows={data}
        rowKey={(b) => b.competitorId}
        loading={isLoading}
        error={error}
        searchable
        searchPlaceholder="Search a hotel or booking engine"
        dense
        emptyMessage="No invalid booking engines detected in the last 7 days"
        expandable={(b) => (
          <KeyValueList
            items={[
              { label: 'Current parameters', value: <code>{b.currentBookingEngineParams ?? '–'}</code> },
              { label: 'Detected parameters', value: <code>{b.newBookingEngineParams ?? '–'}</code> },
            ]}
          />
        )}
      />
      {pending && (
        <ConfirmDialog
          title={pending.decision === 'Accepted' ? 'Apply the detected booking engine' : 'Reject the detected booking engine'}
          message={
            pending.decision === 'Accepted'
              ? `Replace ${pending.entry.currentBookingEngineName} with ${pending.entry.newBookingEngineName} for ${pending.entry.hotelName}? The configuration API will be updated.`
              : `Keep ${pending.entry.currentBookingEngineName} for ${pending.entry.hotelName} and mark the detection as rejected?`
          }
          confirmLabel={pending.decision === 'Accepted' ? 'Apply' : 'Reject'}
          busy={decide.isPending}
          onCancel={() => setPending(null)}
          onConfirm={() => void apply(pending.entry, pending.decision)}
        />
      )}
    </Modal>
  )
}
